import React from "react";
import { Box, Button, Chip, Paper, Stack, Typography, Divider } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

const formatCost = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `$${numeric.toFixed(2)}` : null;
};

function AssistantActionCard({
  actionData,
  messageId,
  onConfirmAddRecord,
  onCreateReminder,
  onCreateIssue,
  onCancelAction,
}) {
  const theme = useTheme();
  const subtleBg = alpha(theme.palette.text.primary, 0.04);

  if (!actionData) return null;

  const { intent, recordDraft, reminderDraft, issueDraft, recordsFound, suggestions, completed } = actionData;

  if (completed) {
    return (
      <Paper
        variant="outlined"
        sx={{ mt: 2, p: 2, backgroundColor: subtleBg }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          This action is completed.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 2, p: 2, backgroundColor: subtleBg }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {intent === "add_record"
          ? "Confirm Maintenance Record"
          : intent === "create_reminder"
          ? "Create Reminder"
          : intent === "diagnose_symptom"
          ? "Review Diagnosis"
          : intent === "search_records"
          ? "Search Results"
          : intent === "maintenance_plan"
          ? "Maintenance Suggestions"
          : "AutoLogg Assistant Action"}
      </Typography>

      {intent === "add_record" && recordDraft && (
        <>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="body2">Service: {recordDraft.serviceType}</Typography>
            <Typography variant="body2">Mileage: {recordDraft.mileage ?? "Not detected"}</Typography>
            <Typography variant="body2">Date: {recordDraft.date}</Typography>
            {recordDraft.cost != null ? (
              <Typography variant="body2">Cost: {formatCost(recordDraft.cost)}</Typography>
            ) : null}
            {recordDraft.notes ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                Notes: {recordDraft.notes}
              </Typography>
            ) : null}
          </Stack>
          {!recordDraft.mileage && (
            <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
              Mileage was not detected. You may want to edit this after saving.
            </Typography>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => onConfirmAddRecord(recordDraft, messageId)}
            >
              Confirm Add Record
            </Button>
            <Button size="small" onClick={() => onCancelAction(messageId)}>
              Cancel
            </Button>
          </Box>
        </>
      )}

      {intent === "create_reminder" && reminderDraft && (
        <>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="body2">Title: {reminderDraft.title}</Typography>
            {reminderDraft.dueDate ? (
              <Typography variant="body2">Due date: {reminderDraft.dueDate}</Typography>
            ) : null}
            {reminderDraft.dueMileage ? (
              <Typography variant="body2">Due mileage: {reminderDraft.dueMileage}</Typography>
            ) : null}
            {reminderDraft.note ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {reminderDraft.note}
              </Typography>
            ) : null}
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => onCreateReminder(reminderDraft, messageId)}
            >
              Create Reminder
            </Button>
            <Button size="small" onClick={() => onCancelAction(messageId)}>
              Cancel
            </Button>
          </Box>
        </>
      )}

      {intent === "diagnose_symptom" && issueDraft && (
        <>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="body2">Title: {issueDraft.title}</Typography>
            <Typography variant="body2">Mileage: {issueDraft.mileage ?? "Unknown"}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {issueDraft.description}
            </Typography>
            {issueDraft.possibleCauses?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Possible causes:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {issueDraft.possibleCauses.map((cause, index) => (
                    <Chip key={index} label={cause} size="small" />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => onCreateIssue(issueDraft, messageId)}
            >
              Create Issue Log
            </Button>
            <Button size="small" onClick={() => onCancelAction(messageId)}>
              Cancel
            </Button>
          </Box>
        </>
      )}

      {recordsFound?.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Matching records:
          </Typography>
          <Stack spacing={1} sx={{ mb: 1 }}>
            {recordsFound.slice(0, 5).map((record, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: 1, backgroundColor: subtleBg }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {record.service_type || record.serviceType || record.type || record.title || "Service"}
                </Typography>
                <Typography variant="body2">Date: {record.date || record.service_date || record.serviceDate || "Unknown"}</Typography>
                <Typography variant="body2">Mileage: {record.mileage ?? record.miles ?? record.odometer ?? "N/A"}</Typography>
                {record.cost != null || record.price != null || record.amount != null ? (
                  <Typography variant="body2">
                    Cost: {formatCost(record.cost ?? record.price ?? record.amount)}
                  </Typography>
                ) : null}
              </Paper>
            ))}
          </Stack>
          {recordsFound.length > 5 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Showing 5 of {recordsFound.length} records.
            </Typography>
          )}
        </>
      )}

      {suggestions?.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Maintenance suggestions:
          </Typography>
          <Stack spacing={1}>
            {suggestions.map((suggestion, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: 1, backgroundColor: subtleBg }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {suggestion.serviceType}
                  </Typography>
                  <Chip
                    label={suggestion.status || "Unknown"}
                    size="small"
                    color={
                      suggestion.status === "overdue"
                        ? "error"
                        : suggestion.status === "due soon"
                        ? "warning"
                        : suggestion.status === "ok"
                        ? "success"
                        : "default"
                    }
                  />
                </Box>
                <Typography variant="body2">{suggestion.reason}</Typography>
                {suggestion.lastServiceMileage != null && (
                  <Typography variant="caption" color="text.secondary">
                    Last service at {suggestion.lastServiceMileage} miles.
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
}

export default AssistantActionCard;
